import { useState } from 'react'
import {X} from 'lucide-react'
import toast from 'react-hot-toast'
import { lumi } from '@/lib/lumi'
import { useAppAuth } from '@/contexts/AuthContext'
import { nowISO } from '@/utils/masks'

interface Cidade { _id: string, nome: string }
interface Secretaria { _id: string, nome: string, cidadeId: string }

interface SubLocalModalProps {
  subLocal?: any
  cidades: Cidade[]
  secretarias: Secretaria[]
  cidadeIdFixa?: string
  onClose: () => void
  onSaved: () => void
}

const TIPOS = [
  { value: 'ESCOLA', label: 'Escola' },
  { value: 'UNIDADE_SAUDE', label: 'Unidade de Saúde' },
  { value: 'UNIDADE_ASSISTENCIA_SOCIAL', label: 'Unidade de Assistência Social' },
  { value: 'OUTRO', label: 'Outro' },
]

function SubLocalModal({ subLocal, cidades, secretarias, cidadeIdFixa, onClose, onSaved }: SubLocalModalProps) {
  const { user: currentUser } = useAppAuth()
  const isEdit = !!subLocal
  const [saving, setSaving] = useState(false)
  const [nome, setNome] = useState(subLocal?.nome || '')
  const [tipo, setTipo] = useState(subLocal?.tipo || 'UNIDADE_SAUDE')
  const [cidadeId, setCidadeId] = useState(subLocal?.cidadeId || cidadeIdFixa || '')
  const [secretariaId, setSecretariaId] = useState(subLocal?.secretariaId || '')
  const [endereco, setEndereco] = useState(subLocal?.endereco || '')
  const [erros, setErros] = useState<Record<string, string>>({})

  const secretariasDaCidade = secretarias.filter(s => s.cidadeId === cidadeId)

  const validar = () => {
    const novosErros: Record<string, string> = {}
    if (!nome.trim()) novosErros.nome = 'Nome do sub local obrigatório.'
    if (!cidadeId) novosErros.cidadeId = 'Selecione uma cidade.'
    if (!secretariaId) novosErros.secretariaId = 'Selecione a secretaria à qual este sub local pertence.'
    setErros(novosErros)
    return Object.keys(novosErros).length === 0
  }

  const handleSubmit = async () => {
    if (!validar()) return
    setSaving(true)
    try {
      const payload = {
        nome: nome.trim(),
        tipo,
        cidadeId,
        secretariaId,
        endereco: endereco.trim(),
        updatedAt: nowISO(),
      }

      if (isEdit) {
        await (lumi.entities as any).sublocais.update(subLocal._id, payload)
        toast.success('Sub local atualizado com sucesso.')
      }
      else {
        await (lumi.entities as any).sublocais.create({
          ...payload,
          creator: currentUser?.userId || 'system',
          createdAt: nowISO(),
        })
        toast.success('Sub local criado com sucesso.')
      }
      onSaved()
      onClose()
    }
    catch {
      toast.error('Erro ao guardar sub local. Tente novamente.')
    }
    finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-[520px] max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E7E9EC]">
          <h2 className="text-[16px] font-semibold text-[#12141A]">
            {isEdit ? 'Editar Sub Local' : 'Novo Sub Local'}
          </h2>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#12141A]">
            <X className="w-5 h-5" strokeWidth={1.75} />
          </button>
        </div>

        <div className="p-6 flex flex-col gap-4">
          <div>
            <label className="block text-[13px] font-medium text-[#374151] mb-1.5">Cidade</label>
            <select
              value={cidadeId}
              onChange={(e) => { setCidadeId(e.target.value); setSecretariaId('') }}
              disabled={!!cidadeIdFixa}
              className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] bg-white disabled:bg-[#F7F8FA] disabled:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]"
            >
              <option value="">Selecione...</option>
              {cidades.map(c => <option key={c._id} value={c._id}>{c.nome}</option>)}
            </select>
            {erros.cidadeId && <p className="text-[12px] text-[#B91C1C] mt-1">{erros.cidadeId}</p>}
          </div>

          <div>
            <label className="block text-[13px] font-medium text-[#374151] mb-1.5">Secretaria</label>
            <select
              value={secretariaId}
              onChange={e => setSecretariaId(e.target.value)}
              disabled={!cidadeId}
              className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] bg-white disabled:bg-[#F7F8FA] disabled:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]"
            >
              <option value="">Selecione...</option>
              {secretariasDaCidade.map(s => <option key={s._id} value={s._id}>{s.nome}</option>)}
            </select>
            <p className="text-[12px] text-[#9CA3AF] mt-1">Ex: Secretaria de Saúde, Secretaria de Assistência Social.</p>
            {erros.secretariaId && <p className="text-[12px] text-[#B91C1C] mt-1">{erros.secretariaId}</p>}
          </div>

          <div>
            <label className="block text-[13px] font-medium text-[#374151] mb-1.5">Tipo</label>
            <select value={tipo} onChange={e => setTipo(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] bg-white focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]">
              {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-[13px] font-medium text-[#374151] mb-1.5">Nome do Sub Local</label>
            <input
              value={nome}
              onChange={e => setNome(e.target.value)}
              placeholder="Ex: Unidade Básica de Saúde, CRAS, Escola Municipal..."
              className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]"
            />
            {erros.nome && <p className="text-[12px] text-[#B91C1C] mt-1">{erros.nome}</p>}
          </div>

          <div>
            <label className="block text-[13px] font-medium text-[#374151] mb-1.5">Endereço (opcional)</label>
            <input
              value={endereco}
              onChange={e => setEndereco(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button onClick={onClose} className="px-4 py-2.5 rounded-lg text-[13.5px] font-medium border border-[#D1D5DB] hover:bg-[#F5F6F8]">
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="px-4 py-2.5 rounded-lg text-[13.5px] font-medium bg-[#1E4FBF] hover:bg-[#1A44A6] text-white disabled:opacity-60"
            >
              {saving ? 'A guardar...' : 'Guardar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SubLocalModal
